import React, { useCallback } from 'react';
import styled from 'styled-components';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import { AvailablePhoneNumber } from 'src/actions/availableNumbers/availableNumbersResource';
import { PhoneNumberCapability } from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { StyledBold, StyledLink } from './shared/elements';
import useSelectInbox from 'src/presentation/components/pages/channels/shared/hooks/useSelectInbox';

const SetupComplete = ({
  closeModal,
  selectedPhone,
  firstStep,
  setInitState,
  stageConfig,
}: {
  closeModal?;
  selectedPhone: AvailablePhoneNumber;
  firstStep?;
  setInitState: () => void;
  stageConfig;
}): JSX.Element | null => {
  const { activateSms } = stageConfig;

  const title = (
    <>
      <StyledBold>{formatPhone(selectedPhone.phoneNumber)}</StyledBold>{' '}
      {activateSms
        ? 'is active and ready to use.'
        : 'will remain inactive. Remember, you can always set up SMS for this number in the future.'}
    </>
  );

  const setUpAnotherNumberHandler = useCallback(() => {
    setInitState();
    firstStep();
  }, [setInitState, firstStep]);

  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Setup complete</H3>
        <StyledDescription>{title}</StyledDescription>
        {activateSms && <TestYourConfigurationAlert phoneNumber={selectedPhone.phoneNumber} />}
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-done" type={Button.Types.PRIMARY} onClick={closeModal}>
          Done
        </Button>
        <Button data-aid="editor-save" type={Button.Types.SECONDARY} onClick={setUpAnotherNumberHandler}>
          Set up another number
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

const TestYourConfigurationAlert = ({ phoneNumber }) => {
  const { inboxName, inboxLink } = useSelectInbox(PhoneNumberCapability.SMS, phoneNumber);

  return (
    <StyledAlertInfo>
      <b>Tip:&nbsp;</b>
      Test your configuration!{' '}
      <StyledLink target="_blank" rel="noopener noreferrer" href={inboxLink}>
        Go to the assigned Inbox
      </StyledLink>{' '}
      for this number, {inboxName}, to confirm that your SMS setup is working as intended.
    </StyledAlertInfo>
  );
};

const StyledDescription = styled.p`
  line-height: 20px;
  margin-bottom: ${p => p.theme.spacing.insetMedium};
`;

const StyledAlertInfo = styled(AlertInfo)`
  line-height: 20px;
  > svg {
    margin-top: 3px;
  }
`;

export default SetupComplete;
